import type { ProdRow } from "../components/OSProductivityTable";

// Log Book по подпроцес (одлука 2026-10-08). A stage whose normativ row carries
// `perSubprocess: true` already holds ALL its minutes — the montage/dismount of every machine
// is folded into that one row (server/utils/logbookPerSubprocess.js), so it must never be
// seeded from the machine codebook again. Stages without such a row (old RN snapshots,
// archived or not-yet-migrated normativs) keep the per-machine seeding unchanged — that is
// what keeps every planned number identical before and after the migration.
export const isPerSubprocessStage = (rows: ProdRow[], stageId: string): boolean =>
  rows.some((row) => row.subprocessId === stageId && row.perSubprocess === true);

type MachineConfig = Array<{ machine: string | { _id: string } | null | undefined; order?: number | string | null }>;

// Configured machines of a subprocess / process definition, in configured order, de-duplicated.
export const orderedMachineIds = (machines?: MachineConfig): string[] => {
  const seen = new Set<string>();
  return [...(machines || [])]
    .sort((a, b) => Number(a.order || 0) - Number(b.order || 0))
    .map((row) => (row.machine && typeof row.machine === "object" ? row.machine._id : row.machine))
    .filter((id): id is string => {
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
};

const TIMING_FIELDS: Array<keyof ProdRow> = [
  "montageStart", "montageEnd",
  "machineStart", "machineEnd",
  "dismountStart", "dismountEnd",
  "cleaningStart", "cleaningEnd",
];

const hasActuals = (row: ProdRow): boolean =>
  TIMING_FIELDS.some((field) => String(row[field] ?? "").trim() !== "") || (row.extraSetupParams || []).length > 0;

// An RN saved BEFORE its normativ moved to one row per subprocess (legacy RN that still
// reads the live normativ) has its actuals stored per machine. Those rows no longer match a
// plan row, so they are kept as actual-only rows (no planned minutes) inside the same stage —
// the grouped Log Book still shows their times and saving keeps them. Only for a per-subprocess
// plan: a per-machine plan keeps its old matching untouched.
export const unmatchedActualRows = (
  stageId: string,
  planRows: ProdRow[],
  savedRows: ProdRow[],
  merge: (plan: ProdRow, saved?: ProdRow) => ProdRow,
  emptyRow: () => ProdRow
): ProdRow[] => {
  const planMachineIds = new Set(planRows.map((row) => row.machineId || ""));
  return savedRows
    .filter((row) => row.subprocessId === stageId && !planMachineIds.has(row.machineId || "") && hasActuals(row))
    .map((saved) => merge({ ...emptyRow(), machineId: saved.machineId, subprocessId: stageId }, saved));
};
